/* eslint-disable react-hooks/exhaustive-deps */
import { toArray } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import useDataTable from '../../../hooks/useDataTable';
import { useDonation } from './ContextAPI';

const DonateQtyInput = React.memo(({ initialValue, availableQty, onCommit }) => {
    const [localValue, setLocalValue] = useState(initialValue ?? '');

    useEffect(() => {
        setLocalValue(initialValue ?? '');
    }, [initialValue]);

    return (
        <Form.Control
            size="sm"
            readOnly={availableQty === 0}
            type="number"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={() => onCommit(localValue)}
        />
    );
});

export default function DrugTable({ drugList }) {
    const { donateData, setDonateData, setDonateList, rowData, setRowData, setRowDataList } =
        useDonation();

    const tableStyle = useMemo(() => ({ paddingLeft: '5px', paddingRight: '5px' }), []);
    const cellStyle = useMemo(
        () => ({ minWidth: '150px', maxWidth: '150px', lineHeight: '33px' }),
        []
    );

    const getRowKey = useCallback((row) => row.drug_id || row.id || row.drug_name, []);

    const getBatchKey = useCallback(
        (row, item, index) =>
            [
                getRowKey(row),
                item.stockin_id || item.id || item.stock_id || item.batch_id || item.batch_no,
                item.batch_no,
                item.expire_date,
                index,
            ]
                .filter((el) => el !== undefined && el !== null)
                .join('_'),
        [getRowKey]
    );

    const getCleanBatch = useCallback((item) => {
        const { donation_qty, ...batchInfo } = item;
        return batchInfo;
    }, []);

    const getDonationQty = useCallback(
        (row, index) => rowData?.[getRowKey(row)]?.batch_info?.[index]?.donation_qty,
        [rowData, getRowKey]
    );

    const donateHandler = useCallback(
        (value, item, row, batchIndex) => {
            const rowKey = getRowKey(row);
            const batchKey = getBatchKey(row, item, batchIndex);

            setDonateData((previousDonateData) => {
                const list = { ...previousDonateData };
                if (value === '' || value === null || value === undefined) {
                    delete list[batchKey];
                } else {
                    list[batchKey] = {
                        ...item,
                        donation_qty: value,
                    };
                }
                return list;
            });

            setRowData((previousRowData) => {
                const rlist = { ...previousRowData };
                const existingRowData = rlist[rowKey];
                const batchInfo = row.batch_info?.map((el, index) => {
                    const currentBatchKey = getBatchKey(row, el, index);
                    const existingDonationQty =
                        existingRowData?.batch_info?.[index]?.donation_qty;
                    const nextDonationQty =
                        currentBatchKey === batchKey ? value : existingDonationQty;

                    if (
                        nextDonationQty === '' ||
                        nextDonationQty === undefined ||
                        nextDonationQty === null
                    ) {
                        return getCleanBatch(el);
                    }

                    return {
                        ...getCleanBatch(el),
                        donation_qty: nextDonationQty,
                    };
                });

                const hasDonationQty = batchInfo?.some(
                    (el) =>
                        el.donation_qty !== '' &&
                        el.donation_qty !== undefined &&
                        el.donation_qty !== null
                );

                if (hasDonationQty) {
                    rlist[rowKey] = {
                        ...row,
                        batch_info: batchInfo,
                    };
                } else {
                    delete rlist[rowKey];
                }
                return rlist;
            });
        },
        [getRowKey, getBatchKey, getCleanBatch, setDonateData, setRowData]
    );

    // Object to Array Convart
    useEffect(() => {
        const arrOfObj = toArray(donateData);
        setDonateList(arrOfObj);
    }, [donateData]);

    // Object to Array Convart
    useEffect(() => {
        const arrOfObj = toArray(rowData);
        setRowDataList(arrOfObj);
    }, [rowData]);

    const ItemTitle = useCallback(() => <span className="ps-2">Item Name</span>, []);

    const Title = useCallback(
        () => (
            <table className="table table-bordered mb-0 text-center" style={tableStyle}>
                <thead>
                    <tr>
                        <th className="m-0" style={cellStyle}>
                            Batch No
                        </th>
                        <th className="m-0" style={cellStyle}>
                            Expire Date
                        </th>
                        <th className="m-0" style={cellStyle}>
                            Available QTY
                        </th>
                        <th className="m-0" style={cellStyle}>
                            Month Consumption
                        </th>
                        <th className="m-0" style={cellStyle}>
                            10% Consumption
                        </th>
                        <th className="m-0" style={cellStyle}>
                            Donate Qty
                        </th>
                    </tr>
                </thead>
            </table>
        ),
        [tableStyle, cellStyle]
    );

    const columns = useMemo(
        () => [
            {
                name: <ItemTitle />,
                cell: (row) => <span className="ps-2">{row.drug_name}</span>,
                sortable: true,
                wrap: true,
                width: '250px',
                fixed: true,
            },
            {
                name: <Title />,
                cell: (row) => (
                    <table className="table table-bordered mb-0 text-center" style={tableStyle}>
                        <tbody>
                            <tr style={{ borderTopWidth: '0px' }}>
                                <td style={cellStyle}>
                                    {row.batch_info.map((el, index) => (
                                        <p key={getBatchKey(row, el, index)}>{el.batch_no}</p>
                                    ))}
                                </td>
                                <td style={cellStyle}>
                                    {row.batch_info.map((el, index) => (
                                        <p key={getBatchKey(row, el, index)}>{el.expire_date}</p>
                                    ))}
                                </td>
                                <td style={cellStyle}>
                                    {row.batch_info.map((el, index) => (
                                        <p key={getBatchKey(row, el, index)}>{el.available_qty}</p>
                                    ))}
                                </td>
                                <td style={cellStyle}>{row.consumption_info?.consumption_ratio}</td>
                                <td style={cellStyle}>{row.consumption_info?.consumption_percentage}</td>
                                <td style={cellStyle}>
                                    {row.batch_info?.map((el, index) => {
                                        const initial = getDonationQty(row, index);
                                        return (
                                            <div key={getBatchKey(row, el, index)}>
                                                <DonateQtyInput
                                                    initialValue={initial}
                                                    availableQty={el.available_qty}
                                                    onCommit={(val) => donateHandler(val, el, row, index)}
                                                />
                                                <br />
                                            </div>
                                        );
                                    })}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                ),
            },
        ],
        [ItemTitle, Title, tableStyle, cellStyle, getBatchKey, getDonationQty, donateHandler]
    );

    const search_column = useMemo(
        () => ({ drug_name: 'Item Name', 'batch_info.batch_no': 'Batch No' }),
        []
    );
    return useDataTable({
        columns,
        data: drugList,
        search_column,
        pagination: false,
        cellPadding: '0px',
        borderWidth: '0px',
        keyField: 'drug_id',
    });
}
